import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AnalyticsEvent, trackEvent } from "shared/analytics";

const STORAGE_KEY_COMPLETED = "quickStartChecklist.completed";
const STORAGE_KEY_DISMISSED = "quickStartChecklist.dismissed";

export const CHECKLIST_ITEM_IDS = [
  "quick-tour",
  "first-search",
  "open-development",
  "save-property",
  "try-filter",
  "check-portfolios",
  // DSH-857: Watchlist + eXchange coverage. Kept in the same flat Set so the
  // localStorage state stays 1:1 mappable to a future Account.onboarding field.
  "view-watchlist",
  "explore-exchange",
] as const;

export type ChecklistItemId = (typeof CHECKLIST_ITEM_IDS)[number];

const readCompleted = (): Set<ChecklistItemId> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_COMPLETED);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((id): id is ChecklistItemId =>
        (CHECKLIST_ITEM_IDS as readonly string[]).includes(id)
      )
    );
  } catch {
    return new Set();
  }
};

const writeCompleted = (ids: Set<ChecklistItemId>) => {
  localStorage.setItem(STORAGE_KEY_COMPLETED, JSON.stringify([...ids]));
};

const readDismissed = (): boolean =>
  localStorage.getItem(STORAGE_KEY_DISMISSED) === "true";

const writeDismissed = (value: boolean) => {
  if (value) {
    localStorage.setItem(STORAGE_KEY_DISMISSED, "true");
  } else {
    localStorage.removeItem(STORAGE_KEY_DISMISSED);
  }
};

const RECENTLY_COMPLETED_TTL_MS = 3000;

export type GuideHighlightMode = "border" | "overlay";

export interface ActiveGuide {
  id: ChecklistItemId;
  selector: string;
  mode: GuideHighlightMode;
  skipIndex: number;
}

interface ChecklistState {
  completed: Set<ChecklistItemId>;
  dismissed: boolean;
  isRevealed: boolean;
  activeGuide: ActiveGuide | null;
  anyRecentlyCompleted: boolean;
  markComplete: (id: ChecklistItemId) => void;
  dismiss: () => void;
  reveal: () => void;
  startGuide: (
    id: ChecklistItemId,
    selector: string,
    mode?: GuideHighlightMode,
    skipIndex?: number
  ) => void;
  endGuide: () => void;
  isComplete: (id: ChecklistItemId) => boolean;
  isRecentlyCompleted: (id: ChecklistItemId) => boolean;
}

const ChecklistContext = createContext<ChecklistState | null>(null);

export const ChecklistProvider = ({ children }: { children: ReactNode }) => {
  const [completed, setCompleted] = useState<Set<ChecklistItemId>>(() =>
    readCompleted()
  );
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());
  const [recentlyCompleted, setRecentlyCompleted] = useState<
    Set<ChecklistItemId>
  >(new Set());
  const [isRevealed, setIsRevealed] = useState(false);
  const [activeGuide, setActiveGuide] = useState<ActiveGuide | null>(null);

  // Sync across tabs / external writes via the storage event.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_COMPLETED) setCompleted(readCompleted());
      if (e.key === STORAGE_KEY_DISMISSED) setDismissed(readDismissed());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const markComplete = useCallback((id: ChecklistItemId) => {
    let wasAlreadyComplete = false;
    setCompleted((prev) => {
      if (prev.has(id)) {
        wasAlreadyComplete = true;
        return prev;
      }
      const next = new Set(prev);
      next.add(id);
      writeCompleted(next);
      return next;
    });
    if (wasAlreadyComplete) return;
    trackEvent(AnalyticsEvent.ONBOARDING_CHECKLIST_ITEM_COMPLETED, {
      item_id: id,
    });
    setRecentlyCompleted((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setTimeout(() => {
      setRecentlyCompleted((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, RECENTLY_COMPLETED_TTL_MS);
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    writeDismissed(true);
  }, []);

  const reveal = useCallback(() => setIsRevealed(true), []);

  const startGuide = useCallback(
    (
      id: ChecklistItemId,
      selector: string,
      mode: GuideHighlightMode = "border",
      skipIndex: number = 0
    ) => setActiveGuide({ id, selector, mode, skipIndex }),
    []
  );

  const endGuide = useCallback(() => setActiveGuide(null), []);

  const isComplete = useCallback(
    (id: ChecklistItemId) => completed.has(id),
    [completed]
  );

  const isRecentlyCompleted = useCallback(
    (id: ChecklistItemId) => recentlyCompleted.has(id),
    [recentlyCompleted]
  );

  const value = useMemo<ChecklistState>(
    () => ({
      completed,
      dismissed,
      isRevealed,
      activeGuide,
      anyRecentlyCompleted: recentlyCompleted.size > 0,
      markComplete,
      dismiss,
      reveal,
      startGuide,
      endGuide,
      isComplete,
      isRecentlyCompleted,
    }),
    [
      completed,
      dismissed,
      isRevealed,
      activeGuide,
      recentlyCompleted,
      markComplete,
      dismiss,
      reveal,
      startGuide,
      endGuide,
      isComplete,
      isRecentlyCompleted,
    ]
  );

  return (
    <ChecklistContext.Provider value={value}>
      {children}
    </ChecklistContext.Provider>
  );
};

export const useChecklistState = (): ChecklistState => {
  const ctx = useContext(ChecklistContext);
  if (!ctx)
    throw new Error("useChecklistState must be used within ChecklistProvider");
  return ctx;
};
